/**
 * Tight vertical viewBox for the ownership graph SVG — crops dead space and
 * reserves clearance for expand chevrons (see OwnershipGraph GraphExpandButton).
 */

import type { GraphEdge } from '../../../types'
import {
  buildCurvedOwnerEdge,
  intermediateNodesSkippedByEdge,
  pointOnCubic,
} from './edgeGeometry'
import type { PositionedNode } from './layoutTree'

/** Must match `LEVEL_H` in layoutTree.ts */
const LEVEL_H = 108
const NODE_H_COMPANY = 46

/** Matches GraphExpandButton: offset 22px + circle r=14 + small margin. */
export const EXPAND_CLEARANCE = 40
export const VIEWPORT_PAD_TIGHT = 16
export const VIEWPORT_PAD_WITH_EXPAND = EXPAND_CLEARANCE

export interface LayoutViewportOptions {
  expandDownNodeIds?: string[]
  expandUpNodeIds?: string[]
}

/** Compress vertical tier spacing on tall graphs (keeps room for edges + % labels). */
export function tierHeightForSpan(levelSpan: number): number {
  if (levelSpan <= 3) return LEVEL_H
  return Math.max(68, Math.min(LEVEL_H, Math.round(LEVEL_H * (3 / levelSpan) + 36)))
}

function sampleEdgeYExtents(
  positions: Record<string, PositionedNode>,
  edges: GraphEdge[],
): { minY: number; maxY: number } {
  let minY = Infinity
  let maxY = -Infinity
  const bump = (y: number) => {
    minY = Math.min(minY, y)
    maxY = Math.max(maxY, y)
  }

  for (const e of edges) {
    if (e.from === e.to) continue
    const from = positions[e.from]
    const to = positions[e.to]
    if (!from || !to) continue
    const intermediates = intermediateNodesSkippedByEdge(
      e.from,
      e.to,
      positions,
      edges,
    )
    const g = buildCurvedOwnerEdge(from, to, 0, intermediates)
    for (const t of [0, 0.15, 0.35, 0.5, 0.65, 0.85, 1]) {
      bump(pointOnCubic(t, g).y)
    }
    bump(g.ly - 10)
    bump(g.ly + 10)
  }

  if (!Number.isFinite(minY)) {
    return { minY: 0, maxY: NODE_H_COMPANY }
  }
  return { minY, maxY }
}

/**
 * Shift all node y-coordinates so content fills the viewBox vertically, with
 * minimal padding and explicit room for expand buttons when requested.
 */
export function finalizeVerticalViewport(
  positions: Record<string, PositionedNode>,
  edges: GraphEdge[],
  rootId: string,
  options?: LayoutViewportOptions,
): number {
  let minY = Infinity
  let maxY = -Infinity
  const bump = (y0: number, y1: number) => {
    minY = Math.min(minY, y0)
    maxY = Math.max(maxY, y1)
  }

  for (const p of Object.values(positions)) {
    bump(p.y, p.y + p.h)
  }

  const edgeExt = sampleEdgeYExtents(positions, edges)
  bump(edgeExt.minY, edgeExt.maxY)

  const expandUpIds = options?.expandUpNodeIds ?? []
  if (expandUpIds.length > 0) {
    let topExpandY = Infinity
    for (const id of expandUpIds) {
      const p = positions[id]
      if (p) topExpandY = Math.min(topExpandY, p.y)
    }
    if (Number.isFinite(topExpandY)) {
      bump(topExpandY - EXPAND_CLEARANCE, topExpandY)
    }
  }

  const expandDownIds = options?.expandDownNodeIds ?? []
  if (expandDownIds.length > 0) {
    let bottomExpandY = -Infinity
    for (const id of expandDownIds) {
      const p = positions[id]
      if (p) bottomExpandY = Math.max(bottomExpandY, p.y + p.h)
    }
    if (Number.isFinite(bottomExpandY)) {
      bump(bottomExpandY, bottomExpandY + EXPAND_CLEARANCE)
    }
  }

  if (!Number.isFinite(minY)) {
    minY = 0
    maxY = NODE_H_COMPANY
  }

  const padTop =
    (options?.expandUpNodeIds?.length ?? 0) > 0
      ? VIEWPORT_PAD_WITH_EXPAND
      : VIEWPORT_PAD_TIGHT
  const padBottom =
    (options?.expandDownNodeIds?.length ?? 0) > 0
      ? VIEWPORT_PAD_WITH_EXPAND
      : VIEWPORT_PAD_TIGHT

  const shift = padTop - minY
  for (const p of Object.values(positions)) {
    p.y += shift
  }

  return maxY - minY + padTop + padBottom
}
